import React, { useState, useEffect } from 'react';
import SearchPayersModal from './SearchPayersModal';
import AddPayerManuallyModal from './AddPayerManuallyModal';
import BatchPayerIntegration from './BatchPayerIntegration';
import { useAuth } from '../../../contexts/AuthContext';
import { db } from '../../../config/firebase';
import { doc, getDoc, setDoc } from 'firebase/firestore';
import { allPayers as payersDatabase } from '../../../data/payersData';
import { getLicensedStates, isPayerRelevantToStates } from '../../../utils/payerStateUtils';
import { useConfirm } from '../../../hooks/useConfirm';

interface Payer {
  id: string;
  payerId: string;
  name: string;
  address: string;
  claimFilingEnrollment: 'ready' | 'not-supported' | 'pending';
  paymentReportEnrollment: 'ready' | 'not-supported' | 'pending';
}

const Payers: React.FC = () => {
  const { user, userProfile } = useAuth();
  const { confirm, ConfirmDialog } = useConfirm();
  const [searchTerm, setSearchTerm] = useState('');
  const [sortBy, setSortBy] = useState<'name' | 'id'>('name');
  const [payers, setPayers] = useState<Payer[]>([]);
  const [filteredPayers, setFilteredPayers] = useState<Payer[]>([]);
  const [showSearchModal, setShowSearchModal] = useState(false);
  const [showAddManuallyModal, setShowAddManuallyModal] = useState(false);
  const [showBatchIntegration, setShowBatchIntegration] = useState(false);
  const [licensedStates, setLicensedStates] = useState<string[]>([]);
  const [showRecommended, setShowRecommended] = useState(false);
  const [recommendedPayers, setRecommendedPayers] = useState<Payer[]>([]);
  const [totalAvailableCount, setTotalAvailableCount] = useState(0);

  // Sample payer data
  const samplePayers: Payer[] = [
    {
      id: '1',
      payerId: '295219',
      name: 'ADVENTHEALTH ADVANTAGE PLANS (HEALTH FIRST HEALTH PLANS)',
      address: '6450 US Highway 1, Rockledge, FL 52955',
      claimFilingEnrollment: 'not-supported',
      paymentReportEnrollment: 'not-supported'
    },
    {
      id: '2',
      payerId: '60054',
      name: 'AETNA',
      address: 'P. O. Box 981106, El Paso, TX 79998-1106',
      claimFilingEnrollment: 'ready',
      paymentReportEnrollment: 'ready'
    },
    {
      id: '3',
      payerId: '57604',
      name: 'Aetna Affordable Health Choices (SM) - SRC',
      address: 'PO Box 14079, Lexington, KY 40512-4079',
      claimFilingEnrollment: 'ready',
      paymentReportEnrollment: 'not-supported'
    },
    {
      id: '4',
      payerId: 'BC001',
      name: 'Anthem Blue Cross of California',
      address: 'P.O. Box 60007, Los Angeles, CA 90060',
      claimFilingEnrollment: 'ready',
      paymentReportEnrollment: 'ready'
    },
    {
      id: '5',
      payerId: 'GABLS',
      name: 'Blue Cross Blue Shield of Georgia (State Health of GA)',
      address: ', , AL',
      claimFilingEnrollment: 'ready',
      paymentReportEnrollment: 'ready'
    }
  ];

  // Load payers from Firestore or use sample data
  useEffect(() => {
    if (!user) return;

    const loadPayers = async () => {
      try {
        const docRef = doc(db, 'billing_settings', 'payers');
        const snap = await getDoc(docRef);
        if (snap.exists() && snap.data().payers) {
          setPayers(snap.data().payers);
        } else {
          setPayers(samplePayers);
          // Save defaults to Firestore
          await setDoc(docRef, { payers: samplePayers, ownerId: user.uid, updatedAt: new Date().toISOString() });
        }
      } catch (err) {
        console.error('Error loading payers:', err);
        setPayers(samplePayers);
      }
    };
    loadPayers();
  }, [user, userProfile]);

  // Load licensed states from Firestore provider profile and compute recommended payers
  const [providerProfile, setProviderProfile] = useState<{ licenses?: Array<{ state?: string }> } | null>(null);

  useEffect(() => {
    if (!user) return;
    const loadProfile = async () => {
      try {
        const profileRef = doc(db, 'providers', user.uid);
        const snap = await getDoc(profileRef);
        if (snap.exists()) {
          setProviderProfile(snap.data() as { licenses?: Array<{ state?: string }> });
        }
      } catch (err) {
        console.error('Error loading provider profile for licensed states:', err);
      }
    };
    loadProfile();
  }, [user]);

  useEffect(() => {
    const states = getLicensedStates(providerProfile);
    setLicensedStates(states);

    if (states.length > 0) {
      const existingIds = new Set(payers.map(p => p.payerId));
      const filtered = payersDatabase
        .filter(p => isPayerRelevantToStates(p, states) && !existingIds.has(p.payerId))
        .sort((a, b) => a.name.toLowerCase().localeCompare(b.name.toLowerCase()));
      setTotalAvailableCount(filtered.length);
      const relevant = filtered
        .slice(0, 50) // Show top 50 suggestions
        .map(p => ({
          ...p,
          claimFilingEnrollment: 'ready' as const,
          paymentReportEnrollment: 'ready' as const,
        }));
      setRecommendedPayers(relevant);
    }
  }, [payers, providerProfile]);

  // Listen for profile updates (licenses changed)
  useEffect(() => {
    const handleProfileUpdate = async () => {
      if (!user) return;
      try {
        const profileRef = doc(db, 'providers', user.uid);
        const snap = await getDoc(profileRef);
        if (snap.exists()) {
          setProviderProfile(snap.data() as { licenses?: Array<{ state?: string }> });
        }
      } catch (err) {
        console.error('Error reloading provider profile:', err);
      }
    };
    window.addEventListener('providerProfileUpdated', handleProfileUpdate);
    return () => window.removeEventListener('providerProfileUpdated', handleProfileUpdate);
  }, [user]);

  // Filter payers based on search
  useEffect(() => {
    let filtered = payers;

    if (searchTerm.trim()) {
      filtered = payers.filter(
        (payer) =>
          payer.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
          payer.payerId.toLowerCase().includes(searchTerm.toLowerCase()) ||
          payer.address.toLowerCase().includes(searchTerm.toLowerCase())
      );
    }

    // Sort - alphabetically by name (case-insensitive)
    filtered = [...filtered].sort((a, b) => {
      if (sortBy === 'name') {
        return a.name.toLowerCase().localeCompare(b.name.toLowerCase());
      } else {
        return a.payerId.toLowerCase().localeCompare(b.payerId.toLowerCase());
      }
    });

    setFilteredPayers(filtered);
  }, [searchTerm, payers, sortBy]);

  const getEnrollmentBadge = (status: 'ready' | 'not-supported' | 'pending') => {
    const styles = {
      ready: { backgroundColor: '#E8F5E9', color: '#2E7D32', text: 'Ready to file claims' },
      'not-supported': { backgroundColor: '#FFF3E0', color: '#E65100', text: 'Not supported' },
      pending: { backgroundColor: '#E3F2FD', color: '#1565C0', text: 'Pending' }
    };

    const style = styles[status];

    return (
      <span
        style={{
          backgroundColor: style.backgroundColor,
          color: style.color,
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 500
        }}
      >
        {style.text}
      </span>
    );
  };

  const getPaymentEnrollmentBadge = (status: 'ready' | 'not-supported' | 'pending') => {
    const styles = {
      ready: { backgroundColor: '#E8F5E9', color: '#2E7D32', text: 'Ready to enroll' },
      'not-supported': { backgroundColor: '#FFF3E0', color: '#E65100', text: 'Not supported' },
      pending: { backgroundColor: '#E3F2FD', color: '#1565C0', text: 'Pending' }
    };

    const style = styles[status];

    return (
      <span
        style={{
          backgroundColor: style.backgroundColor,
          color: style.color,
          padding: '4px 12px',
          borderRadius: '4px',
          fontSize: '13px',
          fontWeight: 500
        }}
      >
        {style.text}
      </span>
    );
  };

  const savePayers = async (updatedPayers: Payer[]) => {
    try {
      const docRef = doc(db, 'billing_settings', 'payers');
      await setDoc(docRef, { payers: updatedPayers, ownerId: user?.uid || '', updatedAt: new Date().toISOString() }, { merge: true });
    } catch (err) {
      console.error('Error saving payers:', err);
    }
  };

  const handleAddPayer = (newPayer: { id: string; payerId: string; name: string; address: string }) => {
    const payerWithEnrollment: Payer = {
      ...newPayer,
      claimFilingEnrollment: 'ready',
      paymentReportEnrollment: 'ready'
    };

    const updatedPayers = [...payers, payerWithEnrollment];
    setPayers(updatedPayers);
    savePayers(updatedPayers);
  };

  const handleRemovePayer = async (payerId: string) => {
    const confirmDelete = await confirm({ title: 'Remove Payer', message: 'Are you sure you want to remove this payer?', confirmLabel: 'Remove', variant: 'danger' });
    if (confirmDelete) {
      const updatedPayers = payers.filter(p => p.id !== payerId);
      setPayers(updatedPayers);
      savePayers(updatedPayers);
    }
  };

  const handleBatchIntegrationComplete = async () => {
    // Reload payers from Firestore to show newly integrated payers
    try {
      const docRef = doc(db, 'billing_settings', 'payers');
      const snap = await getDoc(docRef);
      if (snap.exists() && snap.data().payers) {
        setPayers(snap.data().payers);
      }
    } catch (err) {
      console.error('Error reloading payers:', err);
    }
    setShowBatchIntegration(false);
  };

  return (
    <div style={{ padding: '32px', maxWidth: '1400px' }}>
      {/* Page Header */}
      <div style={{ marginBottom: '16px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' }}>
          <h1 style={{ fontSize: '28px', fontWeight: 700, color: '#FFFFFF', margin: 0 }}>
            Payers
          </h1>
          <div style={{ display: 'flex', gap: '12px' }}>
            <button
              onClick={() => setShowBatchIntegration(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#10B981',
                border: 'none',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer',
                display: 'flex',
                alignItems: 'center',
                gap: '8px'
              }}
            >
              <span>📦</span>
              Batch Integration (554 payers)
            </button>
            <button
              onClick={() => setShowAddManuallyModal(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: 'transparent',
                border: '1px solid #6CA6C1',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Add payer manually
            </button>
            <button
              onClick={() => setShowSearchModal(true)}
              style={{
                padding: '10px 20px',
                backgroundColor: '#1E90FF',
                border: 'none',
                borderRadius: '6px',
                color: '#FFFFFF',
                fontSize: '14px',
                fontWeight: 600,
                cursor: 'pointer'
              }}
            >
              Search available payers
            </button>
          </div>
        </div>
        <p style={{ fontSize: '14px', color: '#87CEEB', margin: '0 0 8px 0' }}>
          Manage insurance payers and enrollments
        </p>
        <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
          <div>
            <span style={{ fontSize: '14px', color: '#B0C4DE' }}>
              View the payers that have been added to your account and manage enrollments.{' '}
            </span>
            <span style={{ fontSize: '14px', color: '#1E90FF' }}>
              Learn more
            </span>
          </div>
          <span
            style={{
              fontSize: '14px',
              color: '#1E90FF',
              display: 'inline-flex',
              alignItems: 'center',
              gap: '6px'
            }}
          >
            <span>🎥</span> Watch a quick video about Payers
          </span>
        </div>
      </div>

      {/* Recommended Payers by Licensed States */}
      {licensedStates.length > 0 && recommendedPayers.length > 0 && (
        <div style={{
          marginBottom: '16px',
          backgroundColor: '#0A2540',
          border: '1px solid #D4A017',
          borderRadius: '8px',
          overflow: 'hidden',
        }}>
          <button
            onClick={() => setShowRecommended(!showRecommended)}
            style={{
              width: '100%',
              padding: '12px 16px',
              backgroundColor: 'transparent',
              border: 'none',
              cursor: 'pointer',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <span style={{ fontSize: '16px', color: '#D4A017', fontWeight: 600 }}>
                Recommended Payers for Your Licensed States
              </span>
              <span style={{
                padding: '2px 8px',
                backgroundColor: '#D4A017',
                color: '#0A2540',
                borderRadius: '12px',
                fontSize: '12px',
                fontWeight: 700,
              }}>
                {licensedStates.join(', ')}
              </span>
              <span style={{
                padding: '2px 8px',
                backgroundColor: '#113F67',
                color: '#87CEEB',
                borderRadius: '12px',
                fontSize: '12px',
              }}>
                {totalAvailableCount} available
              </span>
            </div>
            <span style={{ color: '#D4A017', fontSize: '18px' }}>
              {showRecommended ? '\u25B2' : '\u25BC'}
            </span>
          </button>

          {showRecommended && (
            <div style={{ padding: '0 16px 16px 16px' }}>
              <p style={{ fontSize: '13px', color: '#87CEEB', margin: '0 0 12px 0' }}>
                These payers are relevant to your licensed states. Click "Add" to add them to your payer list.
              </p>
              <div style={{ maxHeight: '400px', overflowY: 'auto' }}>
                <table style={{ width: '100%', borderCollapse: 'collapse' }}>
                  <thead>
                    <tr style={{ borderBottom: '1px solid #2A5470' }}>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#D4A017', width: '100px' }}>Payer ID</th>
                      <th style={{ padding: '8px 12px', textAlign: 'left', fontSize: '12px', fontWeight: 600, color: '#D4A017' }}>Name</th>
                      <th style={{ padding: '8px 12px', textAlign: 'right', fontSize: '12px', fontWeight: 600, color: '#D4A017', width: '80px' }}>Action</th>
                    </tr>
                  </thead>
                  <tbody>
                    {recommendedPayers.map((rp) => (
                      <tr key={rp.id} style={{ borderBottom: '1px solid #1a3a50' }}>
                        <td style={{ padding: '8px 12px', fontSize: '13px', color: '#FFFFFF' }}>{rp.payerId}</td>
                        <td style={{ padding: '8px 12px' }}>
                          <div style={{ fontSize: '13px', color: '#FFFFFF', fontWeight: 500 }}>{rp.name}</div>
                          {rp.address && <div style={{ fontSize: '12px', color: '#87CEEB' }}>{rp.address}</div>}
                        </td>
                        <td style={{ padding: '8px 12px', textAlign: 'right' }}>
                          <button
                            onClick={() => handleAddPayer({ id: rp.id, payerId: rp.payerId, name: rp.name, address: rp.address })}
                            style={{
                              padding: '4px 12px',
                              backgroundColor: '#D4A017',
                              color: '#0A2540',
                              border: 'none',
                              borderRadius: '4px',
                              fontSize: '12px',
                              fontWeight: 600,
                              cursor: 'pointer',
                            }}
                          >
                            + Add
                          </button>
                        </td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            </div>
          )}
        </div>
      )}

      {licensedStates.length === 0 && (
        <div style={{
          marginBottom: '16px',
          padding: '12px 16px',
          backgroundColor: '#113F67',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          fontSize: '13px',
          color: '#87CEEB',
        }}>
          Add your licenses in Profile Settings to see recommended payers for your licensed states.
        </div>
      )}

      {/* Search and Sort */}
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '16px' }}>
        <div style={{ position: 'relative', width: '400px' }}>
          <span
            style={{
              position: 'absolute',
              left: '12px',
              top: '50%',
              transform: 'translateY(-50%)',
              color: '#87CEEB',
              fontSize: '18px'
            }}
          >
            🔍
          </span>
          <input
            type="text"
            placeholder="Search my insurance payers"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            style={{
              width: '100%',
              padding: '10px 12px 10px 40px',
              backgroundColor: '#0F4C75',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#FFFFFF'
            }}
          />
        </div>
        <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
          <span style={{ fontSize: '14px', color: '#B0C4DE' }}>Sort:</span>
          <select
            value={sortBy}
            onChange={(e) => setSortBy(e.target.value as 'name' | 'id')}
            style={{
              padding: '8px 32px 8px 12px',
              backgroundColor: '#0F4C75',
              border: '1px solid #6CA6C1',
              borderRadius: '6px',
              fontSize: '14px',
              color: '#FFFFFF',
              cursor: 'pointer'
            }}
          >
            <option value="name">Name</option>
            <option value="id">Payer ID</option>
          </select>
        </div>
      </div>

      {/* Payers Table */}
      <div
        style={{
          backgroundColor: '#0F3D5C',
          border: '1px solid #6CA6C1',
          borderRadius: '8px',
          overflow: 'hidden'
        }}
      >
        <table style={{ width: '100%', borderCollapse: 'collapse' }}>
          <thead>
            <tr style={{ backgroundColor: '#0A2540', borderBottom: '1px solid #6CA6C1' }}>
              <th
                style={{
                  padding: '10px 12px',
                  textAlign: 'left',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#87CEEB',
                  width: '100px'
                }}
              >
                Payer ID
              </th>
              <th
                style={{
                  padding: '10px 12px',
                  textAlign: 'left',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#87CEEB'
                }}
              >
                Name
              </th>
              <th
                style={{
                  padding: '10px 12px',
                  textAlign: 'left',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#87CEEB',
                  width: '200px'
                }}
              >
                Claim filing enrollment
              </th>
              <th
                style={{
                  padding: '10px 12px',
                  textAlign: 'left',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#87CEEB',
                  width: '220px',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px'
                }}
              >
                Payment report enrollment
                <span style={{ color: '#87CEEB', cursor: 'help', fontSize: '12px' }}>ⓘ</span>
              </th>
              <th
                style={{
                  padding: '10px 12px',
                  textAlign: 'center',
                  fontSize: '13px',
                  fontWeight: 600,
                  color: '#87CEEB',
                  width: '80px'
                }}
              >
                Actions
              </th>
            </tr>
          </thead>
          <tbody>
            {filteredPayers.length === 0 ? (
              <tr>
                <td
                  colSpan={5}
                  style={{
                    padding: '48px 16px',
                    textAlign: 'center',
                    color: '#87CEEB',
                    fontSize: '14px'
                  }}
                >
                  No payers found. Try adjusting your search or add a new payer.
                </td>
              </tr>
            ) : (
              filteredPayers.map((payer) => (
                <tr
                  key={payer.id}
                  style={{
                    borderBottom: '1px solid #2A5470',
                    transition: 'background-color 0.15s'
                  }}
                  onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#113F67')}
                  onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                >
                  <td style={{ padding: '12px', fontSize: '14px', color: '#FFFFFF', fontWeight: 500 }}>
                    {payer.payerId}
                  </td>
                  <td style={{ padding: '12px' }}>
                    <div>
                      <div style={{ fontSize: '14px', color: '#1E90FF', fontWeight: 500, marginBottom: '4px' }}>
                        {payer.name}
                      </div>
                      <div style={{ fontSize: '13px', color: '#B0C4DE' }}>{payer.address}</div>
                    </div>
                  </td>
                  <td style={{ padding: '12px' }}>{getEnrollmentBadge(payer.claimFilingEnrollment)}</td>
                  <td style={{ padding: '12px' }}>{getPaymentEnrollmentBadge(payer.paymentReportEnrollment)}</td>
                  <td style={{ padding: '12px', textAlign: 'center' }}>
                    <button
                      onClick={() => handleRemovePayer(payer.id)}
                      style={{
                        backgroundColor: 'transparent',
                        border: 'none',
                        color: '#EF4444',
                        cursor: 'pointer',
                        fontSize: '18px',
                        padding: '4px 8px',
                        borderRadius: '4px',
                        transition: 'background-color 0.15s'
                      }}
                      onMouseEnter={(e) => (e.currentTarget.style.backgroundColor = '#7F1D1D')}
                      onMouseLeave={(e) => (e.currentTarget.style.backgroundColor = 'transparent')}
                      title="Remove payer"
                    >
                      🗑️
                    </button>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {/* Results count */}
      {filteredPayers.length > 0 && (
        <div style={{ marginTop: '16px', fontSize: '14px', color: '#B0C4DE' }}>
          Showing {filteredPayers.length} of {payers.length} payers
        </div>
      )}

      {/* Search Payers Modal */}
      {showSearchModal && (
        <SearchPayersModal
          onClose={() => setShowSearchModal(false)}
          onAddPayer={handleAddPayer}
          existingPayerIds={payers.map(p => p.payerId)}
        />
      )}

      {/* Add Payer Manually Modal */}
      {showAddManuallyModal && (
        <AddPayerManuallyModal
          onClose={() => setShowAddManuallyModal(false)}
          onSave={(newPayer) => {
            handleAddPayer({
              id: Date.now().toString(),
              payerId: newPayer.payerId,
              name: newPayer.name,
              address: newPayer.address
            });
          }}
        />
      )}

      {/* Batch Payer Integration Modal */}
      {showBatchIntegration && (
        <BatchPayerIntegration
          onClose={() => setShowBatchIntegration(false)}
          onComplete={handleBatchIntegrationComplete}
        />
      )}
      {ConfirmDialog}
    </div>
  );
};

export default Payers;
